
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {DashboardInfoShort} from './DashboardInfoShort';
import {FieldModelDashboardInfoShort} from './DashboardInfoShort';

/**
 * DashboardInfoFull struct
 */
class DashboardInfoFull {
  /**
   * Initialize struct
   * @param {!DashboardInfoShort=} dashboardInfoShort
   * @param {!string=} dashboardSettings
   * @constructor
   */
  constructor (dashboardInfoShort = new DashboardInfoShort(), dashboardSettings = '') {
    this.DashboardInfoShort = dashboardInfoShort
    this.DashboardSettings = dashboardSettings
  }

  /**
   * Copy struct (shallow copy)
   * @this {!DashboardInfoFull}
   * @param {!DashboardInfoFull} other Other struct
   * @returns {!DashboardInfoFull} This struct
   */
  copy (other) {
    if (other.DashboardInfoShort != null) {
      this.DashboardInfoShort = DashboardInfoShort.fromObject(other.DashboardInfoShort)
    } else {
      this.DashboardInfoShort = null
    }
    if (other.DashboardSettings != null) {
      this.DashboardSettings = other.DashboardSettings
    } else {
      this.DashboardSettings = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!DashboardInfoFull}
   * @returns {!DashboardInfoFull} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new DashboardInfoFullModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new DashboardInfoFullModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!DashboardInfoFull}
   * @param {!DashboardInfoFull} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof DashboardInfoFull)) {
      throw new TypeError('Instance of DashboardInfoFull is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!DashboardInfoFull}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      DashboardInfoShort: ((this.DashboardInfoShort != null) ? this.DashboardInfoShort : null),
      DashboardSettings: ((this.DashboardSettings != null) ? this.DashboardSettings : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return DashboardInfoFull.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!DashboardInfoFull} other Object value
   * @returns {!DashboardInfoFull} Created struct
   */
  static fromObject (other) {
    return new DashboardInfoFull().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!DashboardInfoFull}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return DashboardInfoFull.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!DashboardInfoFull}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5176
  }
}

export { DashboardInfoFull };

/**
 * Fast Binary Encoding DashboardInfoFull field model
 */
class FieldModelDashboardInfoFull extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.DashboardInfoShort = new FieldModelDashboardInfoShort(buffer, 4 + 4)
    this.DashboardSettings = new fbe.FieldModelString(buffer, this.DashboardInfoShort.fbeOffset + this.DashboardInfoShort.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelDashboardInfoFull}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelDashboardInfoFull}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.DashboardInfoShort.fbeSize + this.DashboardSettings.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelDashboardInfoFull}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.DashboardInfoShort.fbeExtra + this.DashboardSettings.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelDashboardInfoFull}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelDashboardInfoFull.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelDashboardInfoFull}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5176
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelDashboardInfoFull}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelDashboardInfoFull}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.DashboardInfoShort.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DashboardInfoShort.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DashboardInfoShort.fbeSize

    if ((fbeCurrentSize + this.DashboardSettings.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DashboardSettings.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DashboardSettings.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelDashboardInfoFull}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelDashboardInfoFull}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelDashboardInfoFull}
   * @param {!DashboardInfoFull} fbeValue Default value, defaults is new DashboardInfoFull()
   * @returns {!DashboardInfoFull} DashboardInfoFull value
   */
  get (fbeValue = new DashboardInfoFull()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelDashboardInfoFull}
   * @param {!DashboardInfoFull} fbeValue DashboardInfoFull value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.DashboardInfoShort.fbeSize) <= fbeStructSize) {
      fbeValue.DashboardInfoShort = this.DashboardInfoShort.get()
    } else {
      fbeValue.DashboardInfoShort = new DashboardInfoShort()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DashboardInfoShort.fbeSize

    if ((fbeCurrentSize + this.DashboardSettings.fbeSize) <= fbeStructSize) {
      fbeValue.DashboardSettings = this.DashboardSettings.get()
    } else {
      fbeValue.DashboardSettings = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DashboardSettings.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelDashboardInfoFull}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelDashboardInfoFull}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelDashboardInfoFull}
   * @param {!DashboardInfoFull} fbeValue DashboardInfoFull value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelDashboardInfoFull}
   * @param {DashboardInfoFull} fbeValue DashboardInfoFull value
   */
  setFields (fbeValue) {
    this.DashboardInfoShort.set(fbeValue.DashboardInfoShort)
    this.DashboardSettings.set(fbeValue.DashboardSettings)
  }
}

export { FieldModelDashboardInfoFull };

/**
 * Fast Binary Encoding DashboardInfoFull model
 */
class DashboardInfoFullModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelDashboardInfoFull(this.buffer, 4)
  }

  /**
   * Get the DashboardInfoFull model
   * @this {!DashboardInfoFullModel}
   * @returns {!FieldModelDashboardInfoFull} model DashboardInfoFull model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!DashboardInfoFullModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!DashboardInfoFullModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return DashboardInfoFullModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!DashboardInfoFullModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelDashboardInfoFull.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!DashboardInfoFullModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!DashboardInfoFullModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!DashboardInfoFullModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!DashboardInfoFullModel}
   * @param {!DashboardInfoFull} value DashboardInfoFull value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!DashboardInfoFullModel}
   * @param {!DashboardInfoFull} value DashboardInfoFull value, defaults is new DashboardInfoFull()
   * @return {!object} Deserialized DashboardInfoFull value and its size
   */
  deserialize (value = new DashboardInfoFull()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new DashboardInfoFull(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new DashboardInfoFull(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!DashboardInfoFullModel}
   * @param {!number} prev Previous DashboardInfoFull model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { DashboardInfoFullModel };
